/**
 * Observability for the signed-pixel codec workaround introduced in PR #243.
 *
 * - recordSignCorrectionApplied: counter event; fires when applySignedPixelCorrection
 *   reinterprets a Uint16 buffer as Int16. Tagged with the transfer syntax UID so we
 *   can watch the rate per codec and confirm the workaround can be retired once a
 *   future cornerstone-3d upgrade lands.
 * - warnOnMixedSignedTransferSyntax: dev-only series-assignment guardrail; emits
 *   console.warn when a series mixes transfer syntaxes AND has any signed
 *   instance — the exact pattern that motivated PR #243. Deduped per series so
 *   the warning doesn't spam when slots reassign the same series.
 */

import type { DicomInstanceInfo } from "./dicomMetadataLoader";

/**
 * Counter event for production telemetry. The app uses gtag (Google Analytics);
 * if the user rejected analytics consent, gtag is not loaded and this is a no-op.
 */
export function recordSignCorrectionApplied(transferSyntaxUid: string): void {
  if (typeof window === "undefined") return;
  const gtag = (window as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== "function") return;
  try {
    gtag("event", "sign_correction_applied", {
      transfer_syntax_uid: transferSyntaxUid,
    });
  } catch {
    // Telemetry must never break rendering.
  }
}

const warnedSeriesIds = new Set<string>();

/**
 * Reset the dedup cache. Test-only; not exported via barrel.
 */
export function _resetSignCorrectionWarnings(): void {
  warnedSeriesIds.clear();
}

/**
 * Dev-only warning when a series matches PR #243's exact failure pattern:
 * mixed transfer syntaxes within the series AND at least one signed instance.
 * Helps catch regressions of the same shape early.
 */
export function warnOnMixedSignedTransferSyntax(
  seriesId: string,
  instances: DicomInstanceInfo[] | undefined,
): void {
  if (!import.meta.env.DEV) return;
  if (!instances || instances.length < 2) return;
  if (warnedSeriesIds.has(seriesId)) return;

  const transferSyntaxes = new Set<string>();
  let hasSigned = false;
  for (const instance of instances) {
    if (instance.transfer_syntax_uid) {
      transferSyntaxes.add(instance.transfer_syntax_uid);
    }
    if (instance.pixel_representation === 1) {
      hasSigned = true;
    }
  }

  if (transferSyntaxes.size > 1 && hasSigned) {
    warnedSeriesIds.add(seriesId);
    console.warn(
      `[signCorrection] Series ${seriesId} mixes transfer syntaxes ` +
        `(${Array.from(transferSyntaxes).join(", ")}) and contains signed instances. ` +
        `This is the pattern PR #243 worked around; verify rendering is correct.`,
    );
  }
}
